<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../framework/js/api.js';
  import { toast } from '../framework/js/toast.js';
  import { spinner } from '../framework/js/spinner.js';
  import { push } from 'svelte-spa-router';
  import ModalConfirm from '../framework/components/ModalConfirm.svelte';
  import type { ApiResponse } from '../framework/js/apiTypes.js';

  let proposalRequests: any[] = [];
  let isLoading = false;
  let searchQuery = '';
  let statusFilter = 'all';
  let showDeleteModal = false;
  let deleteTarget: any = null;

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'declined', label: 'Declined' },
  ];

  onMount(async () => {
    await loadProposalRequests();
  });

  async function loadProposalRequests() {
    isLoading = true;
    spinner.show();
    try {
      const response = await api.getProposalRequests(statusFilter, searchQuery) as ApiResponse<unknown[]>;
      if (response.success && response.data !== undefined) {
        proposalRequests = response.data;
      } else {
        toast.showNotification(false, response.message ?? 'Failed to load proposal requests');
      }
    } catch (error) {
      toast.showNotification(false, 'Error loading proposal requests');
      console.error(error);
    } finally {
      spinner.hide();
      isLoading = false;
    }
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'pending':
        return '#f59e0b'; // amber
      case 'contacted':
        return '#056cf2'; // primary blue
      case 'in_progress':
        return '#8b5cf6'; // purple
      case 'completed':
        return '#45c496'; // green
      case 'declined':
        return '#d91b42'; // red
      default:
        return '#7D7D7D'; // grey
    }
  }

  function formatStatus(status: string): string {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  async function updateStatus(uuid: string, newStatus: string) {
    spinner.show();
    try {
      const response = await api.updateProposalRequestStatus(uuid, newStatus);
      if (response.success) {
        toast.showNotification(true, 'Status updated successfully');
        // Update local state
        const index = proposalRequests.findIndex(pr => pr.uuid === uuid);
        if (index !== -1) {
          proposalRequests[index].status = newStatus;
          proposalRequests = [...proposalRequests];
        }
      } else {
        toast.showNotification(false, response.message || 'Failed to update status');
      }
    } catch (error) {
      toast.showNotification(false, 'Error updating status');
      console.error(error);
    } finally {
      spinner.hide();
    }
  }

  function confirmDelete(request: any) {
    deleteTarget = request;
    showDeleteModal = true;
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    
    spinner.show();
    try {
      const response = await api.deleteProposalRequest(deleteTarget.uuid);
      if (response.success) {
        toast.showNotification(true, 'Proposal request deleted');
        proposalRequests = proposalRequests.filter(pr => pr.uuid !== deleteTarget.uuid);
      } else {
        toast.showNotification(false, response.message || 'Failed to delete');
      }
    } catch (error) {
      toast.showNotification(false, 'Error deleting proposal request');
      console.error(error);
    } finally {
      spinner.hide();
      showDeleteModal = false;
      deleteTarget = null;
    }
  }

  function viewBrief(briefUuid: string) {
    push(`/admin/briefs/${briefUuid}?from=requests`);
  }

  function handleSearch() {
    loadProposalRequests();
  }

  function handleFilterChange() {
    loadProposalRequests();
  }

  let searchDebounceTimer: any;
  function debounceSearch() {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
      loadProposalRequests();
    }, 300);
  }
</script>

<div class="page-shell admin-list proposal-list">
  <div class="page-header">
    <div class="header-content">
      <h2 class="page-title">Proposal Requests</h2>
      <p class="page-subtitle">Manage incoming proposal requests from users</p>
    </div>
    <div class="header-stats">
      <span class="stat-badge">{proposalRequests.length} request{proposalRequests.length !== 1 ? 's' : ''}</span>
    </div>
  </div>

  <!-- Filters -->
    <div class="filters-bar">
        <div class="filters-group">
            <div class="search-box">
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    bind:value={searchQuery}
                    on:input={debounceSearch}
                    class="search-input"
                    aria-label="Search proposal requests"
                />
            </div>
            <div class="filter-select">
                <label for="proposal-status-filter" class="sr-only">Filter by status</label>
                <select id="proposal-status-filter" bind:value={statusFilter} on:change={handleFilterChange}>
                    {#each statusOptions as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </div>
        </div>
    </div>

  {#if isLoading}
    <div class="loading-state">Loading proposal requests...</div>
  {:else if proposalRequests.length === 0}
    <div class="empty-state">
      <i class="inbox icon empty-icon" aria-hidden="true"></i>
      <p>No proposal requests found.</p>
      {#if searchQuery || statusFilter !== 'all'}
        <button class="clear-filters-btn" on:click={() => { searchQuery = ''; statusFilter = 'all'; loadProposalRequests(); }}>
          Clear filters
        </button>
      {/if}
    </div>
  {:else}
    <div class="table-wrap requests-table">
      <table class="ui celled table">
        <thead>
          <tr>
            <th>Contact</th>
            <th>Brief</th>
            <th>Notes</th>
            <th>Submitted</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {#each proposalRequests as request (request.uuid)}
            <tr>
              <td class="contact-cell">
                <div class="contact-name">{request.contact_name}</div>
                <div class="contact-email">
                  <a href="mailto:{request.email}">{request.email}</a>
                </div>
                {#if request.phone}
                  <div class="contact-phone">{request.phone}</div>
                {/if}
              </td>
              <td>
                {#if request.brief_uuid}
                  <button 
                    class="brief-link"
                    on:click={() => viewBrief(request.brief_uuid)}
                  >
                    {request.brief_name || 'View Brief'}
                  </button>
                {:else}
                  <span class="text-muted">No brief</span>
                {/if}
              </td>
              <td class="notes-cell">
                {#if request.notes}
                  <div class="notes-text">{request.notes}</div>
                {:else}
                  <span class="text-muted">—</span>
                {/if}
              </td>
              <td class="date-cell">{formatDate(request.created_at)}</td>
              <td>
                <select 
                  class="status-select"
                  value={request.status}
                  style="border-color: {getStatusColor(request.status)}; color: {getStatusColor(request.status)}"
                  on:change={(e) => updateStatus(request.uuid, e.currentTarget.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="declined">Declined</option>
                </select>
              </td>
              <td class="actions-cell">
                  <button
                    type="button"
                    class="action-btn delete-btn"
                    on:click={() => confirmDelete(request)}
                    title="Delete request"
                    aria-label="Delete request from {request.contact_name}"
                  >
                    <i class="trash icon" aria-hidden="true"></i>
                  </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<!-- Delete Confirmation Modal -->
{#if showDeleteModal}
  <ModalConfirm
    bind:showModal={showDeleteModal}
    on:yes={handleDelete}
    on:no={() => { deleteTarget = null; }}
  >
    <svelte:fragment slot="body">
      Are you sure you want to delete this proposal request from {deleteTarget?.contact_name}? This action cannot be undone.
    </svelte:fragment>
  </ModalConfirm>
{/if}

<style>
  .contact-cell {
    min-width: 200px;
  }

  .contact-name {
    font-weight: 500;
    color: var(--grey);
  }

  .contact-email a {
    color: var(--primary);
    text-decoration: none;
    font-size: 0.875rem;
  }

  .contact-email a:hover {
    text-decoration: underline;
  }

  .contact-phone {
    font-size: 0.875rem;
    color: var(--grey-secondary);
  }

  .brief-link {
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    padding: 0;
    font-size: 0.875rem;
    text-decoration: underline;
  }

  .brief-link:hover {
    color: var(--darkprimary);
  }

  .notes-cell {
    max-width: 250px;
  }

  .notes-text {
    font-size: 0.875rem;
    color: var(--grey);
    white-space: pre-wrap;
    word-break: break-word;
  }

  .status-select {
    padding: 0.375rem 0.75rem;
    border-radius: var(--radius-pill);
    border-width: 2px;
    font-size: 0.75rem;
    font-weight: 500;
    background: var(--white);
    cursor: pointer;
  }

  .action-btn {
    padding: 0.375rem;
    border: none;
    border-radius: var(--radius-cta);
    cursor: pointer;
    transition: background-color 0.2s;
    background: transparent;
  }

  .delete-btn {
    color: var(--grey-secondary);
  }

  .delete-btn:hover {
    background: var(--status-danger-soft);
    color: var(--status-danger);
  }

  .delete-btn:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
</style>
